import React, { FC, ReactElement, useEffect, useState } from "react";

import { Stack } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { TextFieldProps } from "@mui/material/TextField";

import { CircleMinus, CirclePlus } from "./icons";
import TextField from "./styled/TextField";

type Props = TextFieldProps & {
  values: string[];
  onSave: (values: string[]) => void;
  validator?: (value: string) => string;
};

const MultiValueTextFieldGroup: FC<React.PropsWithChildren<Props>> = ({
  values,
  onSave,
  validator,
  ...props
}: Props): ReactElement => {
  const [localValues, setLocalValues] = useState(values || []);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setLocalValues(values || []);
    setErrors(
      (values || []).map((value) => (validator && validator(value)) || ""),
    );
  }, [JSON.stringify(values)]);

  const handleAdd = (): void => {
    onSave(localValues.concat([""]));
  };

  const handleChange = (idx: number, value: string): void => {
    const nvs = localValues.map((x) => x) || [];
    nvs[idx] = value;
    setLocalValues(nvs);
    onSave(nvs);
  };

  const handleDelete = (idx: number): void => {
    onSave(localValues.toSpliced(idx, 1));
  };

  const handleBlur = (idx: number, value: string): void => {
    const nvs = localValues.map((x) => x) || [];
    nvs[idx] = value;
    onSave(nvs);
  };

  return (
    <Stack
      sx={{ width: "100%" }}
      spacing={1}
      justifyContent={localValues.length === 0 ? "flex-start" : "space-between"}
    >
      {localValues.length === 0 ? (
        <>
          <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
            <IconButton size="small" onClick={(): void => handleAdd()}>
              <CirclePlus />
            </IconButton>
          </Stack>
        </>
      ) : (
        <>
          {localValues.map((value, i) => (
            <Stack
              key={i}
              direction="row"
              sx={{ width: "100%" }}
              alignItems="start"
            >
              <TextField
                {...props}
                sx={{ flexGrow: 1 }}
                error={!!errors[i]}
                helperText={errors[i]}
                value={value}
                onChange={(
                  evt: React.ChangeEvent<
                    HTMLTextAreaElement | HTMLInputElement
                  >,
                ): void => handleChange(i, evt.target.value)}
                onBlur={(
                  evt: React.ChangeEvent<
                    HTMLTextAreaElement | HTMLInputElement
                  >,
                ): void => handleBlur(i, evt.target.value)}
              />
              <IconButton
                size="small"
                onClick={(): void => handleDelete(i)}
                sx={{ mt: "4px" }}
              >
                <CircleMinus />
              </IconButton>
              <IconButton
                size="small"
                onClick={(): void => handleAdd()}
                sx={{
                  mt: "4px",
                  visibility: i < localValues.length - 1 ? "hidden" : undefined,
                }}
              >
                <CirclePlus />
              </IconButton>
            </Stack>
          ))}
        </>
      )}
    </Stack>
  );
};
export default MultiValueTextFieldGroup;
